import { cache } from 'react';

import { SERVER_API_URL } from '@/lib/public-api-url';
import { SEO_RESOLVE_REVALIDATE_SECONDS } from '../consts/SEO_CACHE';
import type { SeoRedirectResult, SeoResolveResult } from '../types';

const SEO_FETCH_OPTIONS = {
  next: {
    revalidate: SEO_RESOLVE_REVALIDATE_SECONDS,
    tags: ['seo-resolve'],
  },
} as const;

// Server-side fetch to /api/v1/seo/redirect, deduplicated within a single render pass.
// Explicit next.revalidate also feeds the Data Cache used by proxy.ts (outside RSC).
export const getSeoRedirectSSR = cache(async (path: string): Promise<SeoRedirectResult> => {
  try {
    const url = `${SERVER_API_URL}/api/v1/seo/redirect?path=${encodeURIComponent(path)}`;
    const response = await fetch(url, SEO_FETCH_OPTIONS);

    if (response.status === 404) {
      return { status: 'not_found' };
    }

    if (!response.ok) {
      return { status: 'error', error: new Error(`Unexpected status ${response.status}`) };
    }

    const data = await response.json();
    return { status: 'redirect', data };
  } catch (error) {
    return { status: 'error', error };
  }
});

/**
 * Server-side fetch to /api/v1/seo/resolve.
 * Primitive args so React.cache can dedupe generateMetadata + page (Object.is).
 * Explicit next.revalidate keeps Data Cache even when the route is force-dynamic.
 */
export const getSeoResolveSSR = cache(
  async (path?: string, pageKey?: string): Promise<SeoResolveResult> => {
    try {
      const queryParams = new URLSearchParams();
      if (pageKey) queryParams.set('pageKey', pageKey);
      else if (path) queryParams.set('path', path);

      const url = `${SERVER_API_URL}/api/v1/seo/resolve?${queryParams.toString()}`;
      const response = await fetch(url, SEO_FETCH_OPTIONS);

      if (response.status === 404) {
        return { status: 'not_found' };
      }

      if (!response.ok) {
        return { status: 'error', error: new Error(`Unexpected status ${response.status}`) };
      }

      const data = await response.json();
      return { status: 'ok', data };
    } catch (error) {
      return { status: 'error', error };
    }
  },
);
